import { Avatar, Box, Skeleton } from '@radix-ui/themes';
import { MlbStatsHelper } from 'classes/helpers/mlb-stats.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { S3Command } from 'lib_ts/enums/data.enums';
import { RadixAvatarSize } from 'lib_ts/enums/radix-ui';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useHittersStore from 'stores/use-hitters-store';
import useSignedUrlsStore from 'stores/use-signed-urls-store';

export const CommonHitterAvatar = (props: {
  size: RadixAvatarSize;
  hitter: Partial<IHitter>;
  src?: string; // used to override Avatar url (e.g. when user has selected a custom avatar but not yet saved it)
  customFallback?: string;
  clickDisabled?: boolean;
  onClick?: MouseEventHandler;
}) => {
  const { hitter } = props;

  const team = useAuthStore((s) => s.team);
  const loading = useHittersStore((s) => s.loading);

  const color = useMemo(
    () => hitter._mx_mlb_team_details?.colorHex ?? 'var(--gray-4)',
    [hitter._mx_mlb_team_details]
  );

  const textColor = useMemo(
    () => StringHelper.getContrastColor(hitter._mx_mlb_team_details?.colorHex),
    [hitter._mx_mlb_team_details]
  );

  const initials = useMemo(
    () => StringHelper.getInitials(hitter.name),
    [hitter.name]
  );

  const [url, setUrl] = useState<string>();

  useEffect(() => {
    if (team?.permissions?.mlb_stats_api && hitter.playerPk) {
      setUrl(MlbStatsHelper.getPlayerHeadshotUrl(hitter.playerPk));
      return;
    }

    if (hitter.avatar_path) {
      useSignedUrlsStore
        .getState()
        .getSignedUrl(
          hitter.avatar_region ?? AwsRegion.CA,
          hitter.avatar_path,
          S3Command.getObject
        )
        .then((url) => setUrl(url));
      return;
    }

    setUrl(undefined);
  }, [team?.permissions?.mlb_stats_api, hitter.playerPk, hitter.avatar_path]);

  return (
    <Box
      className={props.clickDisabled ? undefined : 'cursor-pointer'}
      onClick={props.clickDisabled ? undefined : (e) => props.onClick?.(e)}
    >
      <Skeleton loading={loading}>
        <Avatar
          src={props.src || url}
          fallback={props.customFallback ?? initials}
          size={props.size}
          style={
            {
              background: `linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%), ${color}`,
              backgroundBlendMode: 'normal, multiply, normal',
              color: textColor,
              '--avatar-fallback-color': textColor,
            } as React.CSSProperties
          }
        />
      </Skeleton>
    </Box>
  );
};

export const AnonymousHitterAvatar = ({ size }: { size: RadixAvatarSize }) => (
  <Avatar
    fallback="N/A"
    size={size}
    color="gray"
    style={{
      color: 'var(--gray-a11)',
      fontSize: `var(--font-size-${size})`,
    }}
  />
);
